import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";

const INDUSTRY_TABS = [
    {
        key: "banking",
        label: "Banking",
        headline: "Banks, NBFCs & financial institutions",
        blurb: "Coordinate self-service and agent support with shared context, consistency, and built-in compliance across Banking & Financial Services.",
        logos: ["hdfcbank", "icicibank", "axisbank", "kotak", "yesbank", "sbi", "paytm", "razorpay"],
    },
    {
        key: "healthcare",
        label: "Healthcare",
        headline: "Payers, providers & life-sciences",
        blurb: "24/7 patient and member self-service with HIPAA-aware intelligent assistance — reducing staff burden while lifting NPS.",
        logos: ["apollo", "fortis", "manipalhospitals", "narayanahealth", "medanta", "cipla", "sunpharma", "biocon"],
    },
    {
        key: "retail",
        label: "Retail & E-commerce",
        headline: "Consumer goods & retail",
        blurb: "Frictionless shopping and optimized operations with instant, personalized support at every touchpoint — pre-purchase to post-delivery.",
        logos: ["flipkart", "amazon", "myntra", "nykaa", "bigbasket", "swiggy", "zomato", "meesho"],
    },
    {
        key: "telecom",
        label: "Telecom & Media",
        headline: "Telecom, media & communications",
        blurb: "Handle billing, plans, complaints and service escalations across voice, SMS and app — while reducing average handle time by 40%.",
        logos: ["airtel", "jio", "vi", "bsnl", "tatasky", "dishtv", "sonyliv", "hotstar"],
    },
    {
        key: "government",
        label: "Government",
        headline: "Public sector & smart cities",
        blurb: "Multilingual citizen services, grievance routing, and tender scrutiny across ministries, PSUs, municipal bodies, and infrastructure agencies.",
        logos: ["gem", "digitalindia", "cppp", "ntpc", "sail", "bharatpetroleum", "indianoil", "hpcl"],
    },
];

export const IndustryLeaders = () => {
    const [active, setActive] = React.useState(INDUSTRY_TABS[0].key);
    const [isHover, setIsHover] = React.useState(false);
    const current = INDUSTRY_TABS.find((t) => t.key === active) || INDUSTRY_TABS[0];

    // Auto-rotate tabs when not hovered
    React.useEffect(() => {
        if (isHover) return;
        const timer = setInterval(() => {
            setActive((prev) => {
                const idx = INDUSTRY_TABS.findIndex((t) => t.key === prev);
                return INDUSTRY_TABS[(idx + 1) % INDUSTRY_TABS.length].key;
            });
        }, 4200);
        return () => clearInterval(timer);
    }, [isHover]);

    return (
        <section
            className="py-24 md:py-32 relative overflow-hidden"
            style={{
                background:
                    "radial-gradient(50% 40% at 15% 20%, rgba(95,168,138,0.16) 0%, transparent 55%), " +
                    "radial-gradient(45% 40% at 90% 85%, rgba(221,209,136,0.20) 0%, transparent 60%), " +
                    "#F5F3E7",
            }}
            data-testid="industry-leaders"
        >
            {/* Floating decorative orbs */}
            <motion.div
                aria-hidden
                className="absolute -top-24 -left-24 w-96 h-96 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(95,168,138,0.14), transparent 70%)" }}
                animate={{ x: [0, 40, 0], y: [0, 20, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                aria-hidden
                className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(46,125,107,0.16), transparent 70%)" }}
                animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
                transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="container-x relative">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <Reveal>
                        <div className="text-eyebrow text-slate-500 mb-4">
                            Trusted by industry leaders
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="heading-section text-3xl sm:text-4xl md:text-5xl">
                            We've built our business by <br />
                            <span className="italic">serving global enterprises.</span>
                        </h2>
                    </Reveal>
                </div>

                {/* Animated tabs with sliding pill indicator */}
                <Reveal delay={0.2}>
                    <div
                        className="flex flex-wrap items-center justify-center gap-2 mb-10 md:mb-12"
                        role="tablist"
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                    >
                        {INDUSTRY_TABS.map((t) => {
                            const isActive = active === t.key;
                            return (
                                <button
                                    key={t.key}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActive(t.key)}
                                    className="relative px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors"
                                    style={{
                                        color: isActive ? "#ffffff" : "#334155",
                                    }}
                                    data-testid={`industry-tab-${t.key}`}
                                >
                                    {isActive && (
                                        <motion.span
                                            layoutId="industry-tab-pill"
                                            className="absolute inset-0 rounded-full"
                                            style={{
                                                background: "linear-gradient(135deg, #0E2A23 0%, #235F52 100%)",
                                                boxShadow: "0 10px 24px -10px rgba(35,95,82,0.55)",
                                            }}
                                            transition={{ type: "spring", stiffness: 380, damping: 32 }}
                                        />
                                    )}
                                    {!isActive && (
                                        <span
                                            className="absolute inset-0 rounded-full bg-white border border-slate-200 hover:border-slate-400 transition-colors"
                                            aria-hidden
                                        />
                                    )}
                                    <span className="relative">{t.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </Reveal>

                {/* Content with cross-fade */}
                <div
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                >
                    <motion.div
                        key={current.key}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="rounded-3xl border border-slate-200 bg-white overflow-hidden"
                        style={{ boxShadow: "0 20px 60px -30px rgba(35,95,82,0.20)" }}
                    >
                            <div className="grid lg:grid-cols-12">
                                {/* Left: copy */}
                                <div className="lg:col-span-5 p-8 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-slate-100">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">
                                        {current.label}
                                    </div>
                                    <h3 className="heading-section text-2xl md:text-3xl text-slate-900 mb-4">
                                        {current.headline}
                                    </h3>
                                    <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-8">
                                        {current.blurb}
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        <a href="/use-cases" className="pill-dark text-sm" data-testid="industry-cta-learn">
                                            Learn more
                                        </a>
                                        <a href="/customer-stories" className="pill-ghost text-sm">
                                            Customer stories
                                        </a>
                                    </div>
                                </div>

                                {/* Right: animated logo grid */}
                                <div className="lg:col-span-7 p-8 md:p-10 lg:p-12 bg-warm">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-6">
                                        Trusted by {current.label.toLowerCase()} leaders
                                    </div>
                                    <motion.div
                                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
                                        initial="hidden"
                                        animate="show"
                                        variants={{
                                            hidden: {},
                                            show: {
                                                transition: { staggerChildren: 0.05, delayChildren: 0.1 },
                                            },
                                        }}
                                    >
                                        {current.logos.map((slug) => (
                                            <motion.div
                                                key={slug}
                                                variants={{
                                                    hidden: { opacity: 0, y: 14, scale: 0.94 },
                                                    show: {
                                                        opacity: 1,
                                                        y: 0,
                                                        scale: 1,
                                                        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                                                    },
                                                }}
                                                whileHover={{ y: -4, scale: 1.04 }}
                                                className="aspect-[3/2] rounded-2xl bg-white border border-slate-100 flex items-center justify-center p-4 transition-colors hover:border-slate-300 hover:shadow-md"
                                                title={slug}
                                            >
                                                <img
                                                    src={`https://cdn.simpleicons.org/${slug}`}
                                                    alt={slug}
                                                    loading="lazy"
                                                    className="max-h-8 md:max-h-10 max-w-full opacity-80 hover:opacity-100 transition-opacity"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = "none";
                                                        if (e.currentTarget.nextSibling)
                                                            e.currentTarget.nextSibling.style.display = "block";
                                                    }}
                                                />
                                                <span
                                                    className="text-xs font-medium text-slate-500 uppercase tracking-wider"
                                                    style={{ display: "none" }}
                                                >
                                                    {slug}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------
   CAPABILITY MATRIX — Kore.ai "Leverage AI with AI" pattern
   9 vertical tabs on the left, each showing headline + body + badge
------------------------------------------------------------------- */
const CAP_MATRIX = [
    { key: "multi-agent",   tab: "Multi-Agent Architecture", headline: "Specialized agents that collaborate — not one model faking it all.", body: "Purpose-built agents divide complex, multi-step work between them, each handling what it does best instead of one model trying to do everything.", badge: "One goal, many agents", icon: "fa-solid fa-diagram-project",
      points: ["Planner, retriever & executor agents in parallel", "Each agent specialized for one job", "Orchestrated toward a single outcome"], metric: { value: "5+", label: "Coordinated agents / workflow" } },
    { key: "workflow",      tab: "Workflow Automation",      headline: "Agents that finish the job — start to end.",                        body: "Nyra executes real business processes from the first step to the last — onboarding, claims, approvals, collections — not isolated tasks.", badge: "End-to-end execution", icon: "fa-solid fa-gears",
      points: ["Runs full processes end-to-end", "Onboarding, claims, approvals & collections", "No dangling half-done tasks"], metric: { value: "100%", label: "Process steps automated" } },
    { key: "knowledge",     tab: "Knowledge Search (RAG)",   headline: "Every answer, grounded in what your organization knows.",           body: "Retrieval-augmented answers pulled from your documents, policies, and systems — each one carrying its sources, never a guess.", badge: "Grounded, with sources", icon: "fa-solid fa-magnifying-glass",
      points: ["Answers grounded in your documents", "Every response cites its sources", "No hallucinated guesses"], metric: { value: "0", label: "Ungrounded guesses" } },
    { key: "integrations",  tab: "Enterprise Integrations",  headline: "It works with the stack you already run.",                         body: "Nyra layers over O365, your CRM, and core systems so agents act on live, real context — it augments what you have, it doesn't replace it.", badge: "O365 · CRM · Core systems", icon: "fa-solid fa-plug",
      points: ["Layers over O365, CRM & core systems", "Acts on live, real-time context", "Augments — never replaces"], metric: { value: "Live", label: "Context from your stack" } },
    { key: "human-loop",    tab: "Human-in-the-Loop",        headline: "AI recommends. People approve. Systems execute.",                  body: "Approval checkpoints sit wherever judgment or accountability is required. You decide exactly which actions need a human signature.", badge: "Accountability by design", icon: "fa-solid fa-user-check",
      points: ["Approval checkpoints where judgment matters", "You choose which actions need sign-off", "AI recommends, people approve"], metric: { value: "You", label: "Stay in control" } },
    { key: "observability", tab: "AI Observability",         headline: "Know what every agent did — and why.",                             body: "Complete audit trails and AI observability let you answer the question a regulator, board member, or customer will always ask.", badge: "100% of interactions logged", icon: "fa-solid fa-clipboard-check",
      points: ["Complete audit trail per interaction", "Know what each agent did and why", "Answer any regulator or board"], metric: { value: "100%", label: "Interactions logged" } },
    { key: "private",       tab: "Private Deployments",      headline: "Your data. Your environment. Your control.",                       body: "Secure private deployments with role-based access keep sensitive information inside your guardrails — built for regulated operations.", badge: "Private & secure", icon: "fa-solid fa-shield-halved",
      points: ["Secure private deployments", "Role-based access control", "Data stays inside your guardrails"], metric: { value: "Private", label: "Your environment" } },
    { key: "multi-channel", tab: "Multi-Channel Deploy",     headline: "One intelligence layer, delivered everywhere.",                    body: "Serve the same grounded intelligence to web, mobile, voice, email, WhatsApp, SMS, and RCS — with no rebuilding per channel.", badge: "6+ channels, one brain", icon: "fa-solid fa-tower-broadcast",
      points: ["Web, mobile, voice & email", "WhatsApp, SMS & RCS", "One brain, no per-channel rebuild"], metric: { value: "6+", label: "Channels, one runtime" } },
];

export const CapabilityMatrix = () => {
    const [active, setActive] = React.useState(CAP_MATRIX[0].key);
    const current = CAP_MATRIX.find((c) => c.key === active) || CAP_MATRIX[0];

    return (
        <section
            className="py-24 md:py-32 relative overflow-hidden"
            style={{
                background:
                    "radial-gradient(60% 45% at 15% 20%, rgba(95,168,138,0.20) 0%, transparent 55%), " +
                    "radial-gradient(55% 45% at 100% 100%, rgba(35,95,82,0.30) 0%, transparent 60%), " +
                    "linear-gradient(135deg, #F5F3E7 0%, #E9E4CB 60%, #C8DDD1 100%)",
            }}
            data-testid="capability-matrix"
        >
            <div className="container-x">
                <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
                    <Reveal>
                        <div className="text-eyebrow text-slate-500 mb-4">The Nyra AI Platform</div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Beyond the chatbot. <span className="italic">The agent that acts.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 text-intro text-slate-600 text-base md:text-lg">
                            Eight capabilities, one intelligence layer — Nyra AI reasons over your
                            knowledge, decides with confidence, keeps a human in the loop, and executes
                            real work across your systems. Not answers that stop at "here's a link,"
                            but outcomes you can measure.
                        </p>
                    </Reveal>
                </div>

                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Left: vertical tabs */}
                    <div className="lg:col-span-4" role="tablist">
                        <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-col">
                            {CAP_MATRIX.map((c) => (
                                <button
                                    key={c.key}
                                    role="tab"
                                    aria-selected={active === c.key}
                                    onClick={() => setActive(c.key)}
                                    className={`text-left px-3 md:px-5 py-2.5 md:py-3.5 rounded-2xl text-[13px] md:text-sm font-medium transition-all flex items-center gap-2.5 md:gap-3 group ${
                                        active === c.key
                                            ? "bg-slate-900 text-white shadow-lg"
                                            : "bg-white/80 backdrop-blur text-slate-700 border border-slate-200 hover:border-slate-400 hover:bg-white"
                                    }`}
                                    data-testid={`cap-tab-${c.key}`}
                                >
                                    <span
                                        className={`w-7 h-7 md:w-8 md:h-8 rounded-lg grid place-items-center shrink-0 transition-colors ${
                                            active === c.key
                                                ? "bg-white/15 text-white"
                                                : "bg-slate-100 text-slate-700 group-hover:bg-slate-200"
                                        }`}
                                    >
                                        <i className={`${c.icon} text-xs`} />
                                    </span>
                                    <span>{c.tab}</span>
                                    {active === c.key && (
                                        <motion.span
                                            layoutId="cap-active-arrow"
                                            className="ml-auto hidden lg:grid place-items-center w-6 h-6 rounded-full bg-white/15 text-white"
                                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                        >
                                            <i className="fa-solid fa-arrow-right text-[10px]" />
                                        </motion.span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Right: active content card */}
                    <div className="lg:col-span-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                className="rounded-3xl border border-slate-200 bg-white overflow-hidden relative lg:sticky lg:top-24"
                                style={{ boxShadow: "0 20px 60px -30px rgba(35,95,82,0.25)" }}
                            >
                                <div className="grid md:grid-cols-5">
                                    {/* LEFT — text content */}
                                    <div className="md:col-span-3 p-7 md:p-9 flex flex-col">
                                        <div className="flex items-center gap-3 mb-5">
                                            <span
                                                className="w-10 h-10 rounded-xl grid place-items-center text-white shrink-0"
                                                style={{ background: "linear-gradient(135deg, #2E7D6B, #235F52)" }}
                                            >
                                                <i className={`${current.icon} text-sm`} />
                                            </span>
                                            <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
                                                {current.tab}
                                            </div>
                                        </div>

                                        <h3 className="heading-section text-2xl md:text-3xl lg:text-[2.1rem] leading-tight text-slate-900 mb-4">
                                            {current.headline}
                                        </h3>
                                        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                                            {current.body}
                                        </p>

                                        {/* Highlight points */}
                                        <div className="mt-6 space-y-3">
                                            {current.points.map((p, i) => (
                                                <motion.div
                                                    key={p}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.12 + i * 0.08, duration: 0.35 }}
                                                    className="flex items-start gap-3"
                                                >
                                                    <span
                                                        className="w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5"
                                                        style={{ background: "rgba(46,125,107,0.12)", color: "#2E7D6B" }}
                                                    >
                                                        <i className="fa-solid fa-check text-[9px]" />
                                                    </span>
                                                    <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
                                                </motion.div>
                                            ))}
                                        </div>

                                        <div className="mt-7 inline-flex self-start items-center gap-3 px-4 py-2.5 rounded-full"
                                            style={{
                                                background: "linear-gradient(135deg, #2E7D6B 0%, #235F52 100%)",
                                                boxShadow: "0 10px 24px -10px rgba(46, 125, 107, 0.55)",
                                            }}
                                        >
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Result</span>
                                            <span className="w-px h-3.5 bg-white/25" />
                                            <span className="text-white text-sm font-medium">{current.badge}</span>
                                        </div>
                                    </div>

                                    {/* RIGHT — animated visual + metric */}
                                    <div
                                        className="md:col-span-2 relative overflow-hidden p-7 md:p-8 flex flex-col items-center justify-center text-center min-h-[260px]"
                                        style={{ background: "linear-gradient(160deg, #0E2A23 0%, #235F52 130%)" }}
                                    >
                                        {/* animated grid glow */}
                                        <div
                                            aria-hidden
                                            className="absolute inset-0 opacity-30 pointer-events-none"
                                            style={{
                                                backgroundImage:
                                                    "radial-gradient(circle at 30% 20%, rgba(201,242,126,0.25), transparent 45%), radial-gradient(circle at 80% 90%, rgba(95,168,138,0.35), transparent 50%)",
                                            }}
                                        />
                                        {/* pulse rings + icon */}
                                        <div className="relative grid place-items-center mb-6">
                                            {[0, 1, 2].map((r) => (
                                                <motion.span
                                                    key={r}
                                                    className="absolute rounded-full border border-white/20"
                                                    style={{ width: 88, height: 88 }}
                                                    initial={{ scale: 0.6, opacity: 0.5 }}
                                                    animate={{ scale: 1.9, opacity: 0 }}
                                                    transition={{ duration: 2.6, repeat: Infinity, delay: r * 0.85, ease: "easeOut" }}
                                                />
                                            ))}
                                            <motion.span
                                                key={current.key + "-icon"}
                                                initial={{ scale: 0.7, rotate: -8, opacity: 0 }}
                                                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                                className="relative w-[76px] h-[76px] rounded-2xl grid place-items-center text-white"
                                                style={{
                                                    background: "rgba(255,255,255,0.08)",
                                                    border: "1px solid rgba(255,255,255,0.18)",
                                                    boxShadow: "0 16px 40px -18px rgba(201,242,126,0.5)",
                                                }}
                                            >
                                                <i className={`${current.icon} text-2xl`} style={{ color: "#C9F27E" }} />
                                            </motion.span>
                                        </div>

                                        {/* metric */}
                                        <motion.div
                                            key={current.key + "-metric"}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.15, duration: 0.4 }}
                                        >
                                            <div className="font-serif text-white text-4xl md:text-5xl leading-none">
                                                {current.metric.value}
                                            </div>
                                            <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 mt-2.5">
                                                {current.metric.label}
                                            </div>
                                        </motion.div>

                                        {/* progress dots */}
                                        <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-1.5">
                                            {CAP_MATRIX.map((d) => (
                                                <span
                                                    key={d.key}
                                                    className="h-1.5 rounded-full transition-all duration-300"
                                                    style={{
                                                        width: d.key === current.key ? 18 : 6,
                                                        background: d.key === current.key ? "#C9F27E" : "rgba(255,255,255,0.25)",
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};

